import React, { useEffect, useState } from "react";
import { BRAND } from "../lib/directorate";
import api from "../lib/api";
import {
    Cpu,
    ShieldCheck,
    Hammer,
    Radio,
    Users,
    GraduationCap,
    TrendingUp,
    Palmtree,
    Music,
    Trophy,
    ArrowUpRight,
} from "lucide-react";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const SECTOR_ICON = {
    finance: TrendingUp,      // Track 01 · Finance · tax.eigooinc.net
    security: ShieldCheck,    // Track 02 · Security · auth.eigooinc.net
    artisan: Hammer,          // Track 03 · Artisan · islandgold.eigooinc.net
    lifestyle: Palmtree,      // Track 04 · Lifestyle · hi.eigooinc.net
    music: Music,             // Track 05 · Music · music.eigooinc.net
    contest: Trophy,          // Track 06 · Contest · siren.eigooinc.net
    // ── Legacy aliases kept so a stale /api/sectors/mass payload doesn't
    //    crash the icon lookup; falls through to Cpu/Radio/etc.
    tech: Cpu,
    defense: ShieldCheck,
    signal: Radio,
    recruitment: Users,
    academy: GraduationCap,
};

/**
 * Sovereign Sectors — six monetization tracks rendered as a hub-level grid.
 * Each card pulls live sector counters from /api/sectors/mass (public,
 * cached on the backend) so visitors see real ROI/Mass numbers across the
 * empire. Falls back gracefully if the endpoint is offline.
 *
 * Mounts after the Lore Injection on the landing hub.
 */
export default function SovereignSectors() {
    const [mass, setMass] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/sectors/mass");
                if (!cancelled) setMass(data);
            } catch {
                /* silent — fall back to descriptive cards */
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section
            data-testid="sovereign-sectors"
            className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20"
        >
            {/* Eyebrow rail */}
            <div className="flex items-center gap-4 mb-8">
                <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
                <div className="text-[10px] uppercase tracking-[0.5em] text-[hsl(45_85%_68%)] font-mono">
                    Six Sovereign Sectors · Six Monetization Tracks
                </div>
                <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.5)]" />
            </div>

            {/* Section title */}
            <div className="text-center max-w-3xl mx-auto mb-12">
                <h2
                    className="font-heading tracking-tighter leading-[0.95] text-foreground"
                    style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
                    data-testid="sectors-headline"
                >
                    The Everything App for{" "}
                    <span className="gold-gradient-text italic">Sovereignty.</span>
                </h2>
                <p
                    className="mt-5 text-lg text-foreground/70"
                    style={cormorant}
                >
                    &ldquo;Every creative vision routes to one of six tracks. Every track is monetized.&rdquo;
                </p>
            </div>

            {/* Sector grid */}
            {/* Sector grid — 6 sectors. 3-up on lg, 2-up on sm, 6-up on xl. */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-px bg-[hsl(40_40%_25%/0.45)] border border-[hsl(40_40%_25%/0.45)]">
                {BRAND.sectors.map((sector, idx) => (
                    <SectorCard
                        key={sector.key}
                        ordinal={String(idx + 1).padStart(2, "0")}
                        sector={sector}
                        stats={mass?.sectors?.[sector.key]}
                    />
                ))}
            </div>

            {/* Aggregate strip */}
            {mass?.totals && (
                <div
                    data-testid="sectors-totals"
                    className="mt-10 grid sm:grid-cols-3 gap-px bg-[hsl(40_40%_25%/0.45)] border border-[hsl(40_40%_25%/0.45)]"
                >
                    <TotalCell
                        label="Total ROI"
                        value={fmt$(mass.totals.roi || 0)}
                        accent="hsl(45 85% 68%)"
                    />
                    <TotalCell
                        label="Total Signals · Mass"
                        value={(mass.totals.mass || 0).toLocaleString()}
                        accent="hsl(45 85% 75%)"
                    />
                    <TotalCell
                        label="Tracks Active"
                        value={`${mass.totals.active_tracks || 0} / 5`}
                        accent="hsl(153 60% 60%)"
                    />
                </div>
            )}
        </section>
    );
}

function SectorCard({ ordinal, sector, stats }) {
    const Icon = SECTOR_ICON[sector.key] || Cpu;
    const roi = stats?.roi || 0;
    const massCount = stats?.mass || 0;
    const hasData = stats && (roi > 0 || massCount > 0);

    // Strike #24 · Hot-Wire Sovereign Tracks
    // Every Track card is now a functional link to its Sovereign Node.
    // The card wrapper is an <a> so the whole surface is clickable,
    // AND a Liquid Gold "ENGAGE" CTA is rendered in the card footer
    // so the affordance is unambiguous on touch devices.
    const href = sector.url;
    const hasUrl = Boolean(href);

    const cardProps = hasUrl
        ? {
              href,
              target: "_blank",
              rel: "noopener noreferrer",
              "data-testid": `sector-${sector.key}`,
              "aria-label": `${sector.label} · Engage Sovereign Node`,
              className:
                  "relative group p-6 lg:p-7 bg-[hsl(0_0%_5%/0.85)] hover:bg-[hsl(0_0%_8%/0.95)] transition-all duration-300 flex flex-col cursor-pointer",
          }
        : {
              "data-testid": `sector-${sector.key}`,
              className:
                  "relative group p-6 lg:p-7 bg-[hsl(0_0%_5%/0.85)] hover:bg-[hsl(0_0%_8%/0.95)] transition-colors flex flex-col",
          };

    const Tag = hasUrl ? "a" : "article";

    return (
        <Tag {...cardProps}>
            <div className="flex items-start justify-between mb-5">
                <span className="text-[10px] uppercase tracking-[0.4em] font-mono text-muted-foreground/60">
                    Track / {ordinal}
                </span>
                <span
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{
                        background: "rgba(0,0,0,0.35)",
                        border: `1px solid ${sector.accent}55`,
                    }}
                >
                    <Icon size={15} style={{ color: sector.accent }} />
                </span>
            </div>

            <h3
                className="font-heading text-3xl tracking-tight leading-tight"
                style={{ color: sector.accent }}
            >
                {sector.label}
            </h3>
            <div className="mt-1 text-[11px] uppercase tracking-[0.3em] text-muted-foreground/80 font-mono">
                {sector.short}
            </div>

            <p
                className="mt-4 text-sm text-foreground/65 leading-relaxed flex-1"
                style={cormorant}
            >
                {sector.description}
            </p>

            {/* Stats footer */}
            <div className="mt-6 pt-4 border-t border-[hsl(40_40%_25%/0.3)] flex items-center justify-between gap-3">
                <div>
                    <div className="text-[9px] uppercase tracking-[0.35em] text-muted-foreground/60 font-mono">
                        ROI
                    </div>
                    <div
                        className="font-mono text-base mt-0.5"
                        style={{ color: hasData ? sector.accent : "hsl(40 25% 55% / 0.6)" }}
                    >
                        {fmt$(roi)}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[9px] uppercase tracking-[0.35em] text-muted-foreground/60 font-mono">
                        Mass
                    </div>
                    <div
                        className="font-mono text-base mt-0.5"
                        style={{ color: hasData ? "hsl(45 85% 75%)" : "hsl(40 25% 55% / 0.6)" }}
                    >
                        {massCount.toLocaleString()}
                    </div>
                </div>
                {hasData && (
                    <TrendingUp size={13} style={{ color: sector.accent, opacity: 0.7 }} />
                )}
            </div>

            {/* ENGAGE CTA · Strike #24 · Liquid Gold (#C9A227) — gold border,
                gold text, hover fills gold with deep obsidian text. */}
            {hasUrl && (
                <div
                    className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 uppercase tracking-[0.3em] text-[11px] font-bold transition-all duration-300"
                    style={{
                        fontFamily: "'Montserrat', sans-serif",
                        color: "#C9A227",
                        border: "1px solid #C9A227",
                        background: "transparent",
                        minHeight: 44,
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#C9A227";
                        e.currentTarget.style.color = "#0A0A0A";
                        e.currentTarget.style.boxShadow = "0 0 18px rgba(201,162,39,0.55)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "#C9A227";
                        e.currentTarget.style.boxShadow = "none";
                    }}
                    data-testid={`sector-engage-${sector.key}`}
                >
                    Engage <ArrowUpRight size={13} strokeWidth={2.5} />
                </div>
            )}

            {/* Top hairline */}
            <span
                aria-hidden="true"
                className="absolute top-0 left-0 right-0 h-px opacity-50 group-hover:opacity-100 transition-opacity"
                style={{ background: sector.accent }}
            />
        </Tag>
    );
}

function TotalCell({ label, value, accent }) {
    return (
        <div className="p-5 bg-[hsl(0_0%_5%/0.85)]">
            <div className="text-[9px] uppercase tracking-[0.4em] text-muted-foreground/70 font-mono mb-1">
                {label}
            </div>
            <div
                className="font-heading text-3xl tracking-tight"
                style={{ color: accent }}
            >
                {value}
            </div>
        </div>
    );
}

function fmt$(n) {
    if (!n || n < 1) return "$0";
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${Math.round(n).toLocaleString()}`;
}
